import React from 'react';
import { X, Lightbulb } from 'lucide-react';
import { ResolveCategoryType, ResolveSuggestedPrompt } from '../../constants/resolveSuggestedPrompts';
import { MAGNUS_EMOJI_VARIANTS } from '../../constants/MagnusResolveAssistantConstants';

interface SuggestedPromptsPanelProps {
    showSuggestedPrompts: boolean;
    setShowSuggestedPrompts: (show: boolean) => void;
    selectedCategory: ResolveCategoryType | null;
    setSelectedCategory: (cat: ResolveCategoryType | null) => void;
    allPrompts: ResolveSuggestedPrompt[];
    filteredPrompts: ResolveSuggestedPrompt[];
    categories: any[];
    handlePromptClick: (prompt: ResolveSuggestedPrompt) => void;
}

export const SuggestedPromptsPanel: React.FC<SuggestedPromptsPanelProps> = ({
    showSuggestedPrompts,
    setShowSuggestedPrompts,
    selectedCategory,
    setSelectedCategory,
    allPrompts,
    filteredPrompts,
    categories,
    handlePromptClick
}) => {
    if (!showSuggestedPrompts) return null;

    return (
        <div className="fixed bottom-24 right-6 z-40 w-96 max-h-[70vh] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden border-2 border-yellow-200 dark:border-yellow-700">
            {/* Header */}
            <div className="p-4 bg-gradient-to-r from-yellow-400 to-amber-500 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Lightbulb className="text-white" size={24} />
                    <div>
                        <h3 className="text-white font-bold text-lg">Suggested Prompts</h3>
                        <p className="text-yellow-100 text-xs">Reliability Engineering Tasks</p>
                    </div>
                </div>
                <button
                    onClick={() => setShowSuggestedPrompts(false)}
                    className="text-white/80 hover:text-white transition-colors"
                >
                    <X size={20} />
                </button>
            </div>

            {/* Categories */}
            <div className="p-3 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => setSelectedCategory(null)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedCategory === null
                            ? 'bg-yellow-500 text-white'
                            : 'bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600'
                            }`}
                    >
                        All ({allPrompts.length})
                    </button>
                    {categories.map((cat) => {
                        const count = allPrompts.filter(p => p.category === cat.id).length;
                        return (
                            <button
                                key={cat.id}
                                onClick={() => setSelectedCategory(cat.id)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedCategory === cat.id
                                    ? cat.activeColor
                                    : cat.color
                                    }`}
                            >
                                {cat.icon} {cat.id.split(' ')[0]} ({count})
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Prompts List */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {filteredPrompts.map((prompt) => (
                    <button
                        key={prompt.id}
                        onClick={() => handlePromptClick(prompt)}
                        className="w-full text-left p-3 bg-gray-50 dark:bg-slate-700 hover:bg-yellow-50 dark:hover:bg-slate-600 rounded-lg transition-all border border-gray-200 dark:border-slate-600 hover:border-yellow-400"
                    >
                        <div className="flex items-start gap-2">
                            <div className="mt-0.5">{prompt.icon}</div>
                            <div className="flex-1">
                                <h4 className="font-semibold text-sm text-gray-900 dark:text-white mb-1">
                                    {prompt.title}
                                </h4>
                                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                                    {prompt.description}
                                </p>
                            </div>
                        </div>
                    </button>
                ))}
                {filteredPrompts.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <Lightbulb className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No prompts in this category</p>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">
                <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
                    💡 Click any prompt to use it • {MAGNUS_EMOJI_VARIANTS.resolve} Magnus Resolve AI
                </p>
            </div>
        </div>
    );
};
